import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { useStateContext } from '../contexts/ContextProvider';

export default function LoginForm() {
    const { setUser, setToken } = useStateContext();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationError('');

        if (!email || !password) {
            setError('Veuillez remplir tous les champs.');
            return;
        }

        if (!validateEmail(email)) {
            setError('Veuillez entrer une adresse e-mail valide.');
            return;
        }

        setLoading(true);

        try {
            const { data } = await axiosClient.post('/request/user/organizer/login', { email, password });

            if (data.status !== 200 || !data.auth || !data.user || !data.token) {
                setValidationError('Une erreur est survenue, veuillez réessayer.');
                return;
            }

            if (!data.user.is_confirmed_with_email) {
                setValidationError(
                    "Votre compte n'est pas encore vérifié. Redirection vers la vérification…",
                );
                setTimeout(() => {
                    navigate('/verify-auth-code', {
                        state: { email, action: 'account_verification' },
                    });
                }, 2500);
                return;
            }

            setToken(data.token);
            setUser(data.user);
            navigate('/dashboard', { replace: true });
        } catch (err) {
            const response = err.response;
            if (response?.status === 422) {
                setValidationError('Veuillez vérifier vos informations puis réessayer.');
            } else if (response?.data?.message) {
                setValidationError(response.data.message);
            } else if (!response) {
                setValidationError('Connexion impossible. Vérifiez votre réseau ou réessayez.');
            } else {
                setValidationError('Une erreur est survenue, veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    const isSubmitDisabled = () => loading || !email || !password;

    return (
        <>
            <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit}>
                <label htmlFor="email" className="block">
                    <span className="text-neutral-800 dark:text-neutral-200 text-sm">
                        Adresse e-mail <span className="text-red-600">*</span>
                    </span>
                    <input
                        type="email"
                        className="block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900 rounded-2xl text-sm font-normal h-11 px-4 py-3 mt-1"
                        placeholder="example@example.com"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                    />
                    {error && (
                        <small className="text-red-600 mt-2 block">{error}</small>
                    )}
                </label>
                <label className="block" htmlFor="password">
                    <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200 text-sm">
                        <div>
                            Mot de passe <span className="text-red-600">*</span>
                        </div>
                        <Link className="text-sm" to="/forgot-password">
                            Mot de passe oublié?
                        </Link>
                    </span>
                    <input
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type="password"
                        required
                        placeholder="Entrez votre mot de passe"
                        autoComplete="current-password"
                        className="block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900 rounded-2xl text-sm font-normal h-11 px-4 py-3 mt-1"
                    />
                </label>
                <div className="inline-flex items-center justify-center flex-col">
                    {validationError && (
                        <div
                            className="px-2.5 py-1 p-3 mt-3 mb-6 flex items-center justify-center nc-Badge rounded-full font-medium text-xs text-red-800 bg-red-100"
                            style={{ width: 'fit-content' }}
                        >
                            <span className="text-sm ml-3 mr-3">{validationError}</span>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="nc-Button relative h-auto w-full inline-flex items-center justify-center rounded-full transition-colors text-sm sm:text-base font-medium px-4 py-3 sm:px-6 ttnc-ButtonPrimary disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-6000 dark:focus:ring-offset-0"
                        disabled={isSubmitDisabled()}
                    >
                        {loading ? (
                            <>
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 ml-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx={12}
                                        cy={12}
                                        r={10}
                                        stroke="currentColor"
                                        strokeWidth={3}
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                Connexion en cours...
                            </>
                        ) : (
                            <>Connexion</>
                        )}
                    </button>
                </div>
            </form>
        </>
    );
}
